/**
 * RouterNode - Smart Router node for intent_router condition nodes
 * Renders N dynamic handles (one per intent) + 1 default (fallback) handle
 */

import { memo, useMemo, useEffect } from 'react';
import { Handle, Position, useEdges, useUpdateNodeInternals } from '@xyflow/react';
import { ICON_MAP } from '../../constants/iconMap';

const RouterNode = ({ id, data, selected }) => {
  const edges = useEdges();
  const updateNodeInternals = useUpdateNodeInternals();
  const intents = data.config?.intents || [];

  // Force React Flow to recalculate handle positions when intents change
  useEffect(() => {
    updateNodeInternals(id);
  }, [id, intents.length, intents.map(i => i.id).join(','), updateNodeInternals]);

  // Memoize connection status for each handle
  const connectionStatus = useMemo(() => {
    const nodeEdges = edges.filter(e => e.source === id);
    const status = {};
    intents.forEach(intent => {
      status[intent.id] = nodeEdges.some(e => e.sourceHandle === intent.id);
    });
    status.default = nodeEdges.some(e => e.sourceHandle === 'default');
    return status;
  }, [edges, id, intents]);

  const Icon = ICON_MAP[data.iconKey] || ICON_MAP['intent_router'] || ICON_MAP['intent'];

  return (
    <div className={`afc-node afc-node-router ${selected ? 'selected' : ''}`}>
      {/* Handle de entrada (topo) */}
      <Handle
        type="target"
        position={Position.Top}
        className="afc-handle afc-handle-target"
      />

      {/* Conteúdo do nó */}
      <div className="afc-router-accent" style={{ background: data.gradient }} />
      <div className="afc-router-inner">
        <div className="afc-node-icon-wrapper small" style={{ background: data.gradient }}>
          {Icon && <Icon size={16} stroke={1.5} />}
        </div>
        <div className="afc-node-text">
          <div className="afc-node-label">{data.label}</div>
          <div className="afc-node-subtitle">{intents.length} rota{intents.length !== 1 ? 's' : ''} + fallback</div>
        </div>
      </div>

      {/* Handles dinâmicos à direita (1 por intent) */}
      {intents.map((intent, i) => {
        const percent = ((i + 1) / (intents.length + 1)) * 100;
        const isConnected = connectionStatus[intent.id];
        // Nome base: usa `label` (nome amigável do tipo) ou cai no id.
        const base = (intent.label && intent.label.trim()) ? intent.label : intent.id;
        // Sufixo numérico quando é rota duplicada (scheduling_2 → "· 2")
        let suffix = '';
        if (intent.id && intent.key) {
          const m = intent.id.match(new RegExp(`^${intent.key}_(\\d+)$`));
          if (m) suffix = ` · ${m[1]}`;
        }
        const displayName = `${base}${suffix}`;
        // Título (tooltip) prioriza description, depois label+id
        const tooltipText = intent.description
          || (intent.label ? `${intent.label} (${intent.id})` : intent.id);
        return (
          <div key={intent.id}>
            <Handle
              type="source"
              position={Position.Right}
              id={intent.id}
              className={`afc-handle afc-handle-source router-intent ${isConnected ? 'connected' : 'disconnected'}`}
              style={{ top: `${percent}%` }}
              title={tooltipText}
            />
            <div
              className={`afc-router-handle-label ${isConnected ? 'connected' : ''}`}
              style={{ top: `${percent}%` }}
              title={tooltipText}
            >
              {ICON_MAP[intent.id] ? (() => {
                const IntentIcon = ICON_MAP[intent.id];
                return <IntentIcon size={12} stroke={1.5} />;
              })() : null}
              <span>{displayName}</span>
            </div>
          </div>
        );
      })}

      {/* Handle default (fallback) no bottom */}
      <Handle
        type="source"
        position={Position.Bottom}
        id="default"
        className={`afc-handle afc-handle-source router-default ${connectionStatus.default ? 'connected' : 'disconnected'}`}
        title="Fallback / FAQ"
      />
      <div className={`afc-router-default-label ${connectionStatus.default ? 'connected' : ''}`}>
        Fallback
      </div>
    </div>
  );
};

export default memo(RouterNode);
